/**
 * Browser Performance API instrumentation for the Sportsnew detail/list
 * flow. Every function here is a no-op when `performance` isn't available
 * (SSR, Node test runs) so it's always safe to call unconditionally from
 * SportsnewContext.
 *
 * Marks/measures produced (inspect via the Performance tab, or
 * `performance.getEntriesByName(name)` / `window.__sportsnewPerf` in the
 * console):
 *   - list_loaded                 — fetchFixtures() resolved and fixtures were set
 *   - click_to_detail_header      — match click -> detail shell/header visible
 *   - click_to_first_market       — match click -> first market group rendered
 *   - full_detail_loaded          — match click -> every market group rendered
 *   - ws_patch_to_render          — a WS patch arriving -> its state update committing
 *   - restRefetchAfterWsPatchCount (window.__sportsnewPerf, not a mark) — how many
 *     times a WS patch triggered a debounced REST refetch this session; should stay
 *     at/near 0 under normal operation (see scheduleListRefetch in SportsnewContext).
 */

const hasPerf = () => typeof performance !== 'undefined' && typeof performance.mark === 'function';

/** Debug surface for manual inspection in the browser console. */
export const getPerfDebug = () => {
  if (typeof window === 'undefined') return null;
  if (!window.__sportsnewPerf) {
    window.__sportsnewPerf = { restRefetchAfterWsPatchCount: 0, measures: {} };
  }
  return window.__sportsnewPerf;
};

// Keep only the most recent N samples per measure name in the debug surface —
// a long-lived session can generate thousands of WS-patch measures, and an
// unbounded array here would itself be a memory leak.
const MAX_SAMPLES_PER_MEASURE = 50;

const recordMeasure = (name, durationMs) => {
  const debug = getPerfDebug();
  if (!debug) return;
  if (!debug.measures[name]) debug.measures[name] = [];
  debug.measures[name].push(Math.round(durationMs * 100) / 100);
  if (debug.measures[name].length > MAX_SAMPLES_PER_MEASURE) {
    debug.measures[name] = debug.measures[name].slice(-MAX_SAMPLES_PER_MEASURE);
  }
};

export const markPerf = (name) => {
  if (!hasPerf()) return;
  try { performance.mark(name); } catch { /* ignore */ }
};

/** Mark `endName` now and measure `measureName` from `startName` to it, recording
 * the duration into window.__sportsnewPerf.measures for later inspection. Clears
 * the underlying mark/measure entries immediately after reading them — this is
 * short-lived debug instrumentation, not permanent monitoring, so the browser's
 * own Performance entry buffer must not grow unbounded over a long session
 * (a fixture detail panel or WS patch stream can fire thousands of these).
 *
 * `clearStart` must be false when several measures share the same start mark
 * (e.g. click_to_detail_header / click_to_first_market / full_detail_loaded
 * all measure from the same click) — only the LAST of them should clear it. */
export const measurePerf = (measureName, startName, endName, { clearStart = true } = {}) => {
  if (!hasPerf()) return;
  try {
    performance.mark(endName);
    const entry = performance.measure(measureName, startName, endName);
    if (entry?.duration != null) recordMeasure(measureName, entry.duration);
  } catch { /* startName mark may not exist (e.g. clicked before list_loaded) — ignore */ }
  finally {
    try {
      if (clearStart) performance.clearMarks(startName);
      performance.clearMarks(endName);
      performance.clearMeasures(measureName);
    } catch { /* ignore */ }
  }
};

/** Per-fixture click marks: click_to_detail_header, click_to_first_market,
 * full_detail_loaded are all measured from the SAME click mark — only the
 * last one to fire (full_detail_loaded, or an error abandoning the other
 * two) clears the shared start mark. */
export const markDetailClick = (fixtureId) => markPerf(`sportsnew:click:${fixtureId}`);
export const measureDetailHeader = (fixtureId) =>
  measurePerf('click_to_detail_header', `sportsnew:click:${fixtureId}`, `sportsnew:header:${fixtureId}`, { clearStart: false });
export const measureFirstMarket = (fixtureId) =>
  measurePerf('click_to_first_market', `sportsnew:click:${fixtureId}`, `sportsnew:first_market:${fixtureId}`, { clearStart: false });
export const measureFullDetailLoaded = (fixtureId) =>
  measurePerf('full_detail_loaded', `sportsnew:click:${fixtureId}`, `sportsnew:full_detail:${fixtureId}`);

/** Safety cleanup for the shared click-start mark if an error/abort means
 * full_detail_loaded (the normal clearer) never fires for this fixture. */
export const clearDetailClickMark = (fixtureId) => {
  if (!hasPerf()) return;
  try { performance.clearMarks(`sportsnew:click:${fixtureId}`); } catch { /* ignore */ }
};

/** WS-patch-to-render: mark at patch receipt, measure after the state update
 * that applies it has committed (approximated via requestAnimationFrame,
 * since React's actual commit/paint isn't otherwise observable from here). */
export const markWsPatchReceived = (kind, seq) => markPerf(`sportsnew:ws_patch:${kind}:${seq}`);
export const measureWsPatchToRender = (kind, seq) => {
  if (!hasPerf()) return;
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
  raf(() => {
    measurePerf('ws_patch_to_render', `sportsnew:ws_patch:${kind}:${seq}`, `sportsnew:ws_patch_rendered:${kind}:${seq}`);
  });
};

export const markListLoaded = () => {
  if (!hasPerf()) return;
  try {
    performance.mark('list_loaded');
  } catch { /* ignore */ }
};
